import React, { useState, useEffect, useContext } from "react";
import "./SignUp.css";
import { Link, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  OAuthProvider,
} from "firebase/auth";
import { functions, auth } from "../../../config/firebaseConfig";
import microsoftLogo from "../../../assets/microsoft-logo.png";
import { UserContext } from "../../../App";

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [agreedToEssential, setAgreedToEssential] = useState(false);
  const [agreedToOptional, setAgreedToOptional] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);

  const handleNext = (e) => {
    e.preventDefault();
    if (email && password && agreedToEssential) {
      setStep(2);
    } else {
      alert(
        "Please fill in all required fields and agree to the essential data collection to continue."
      );
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (step === 1 && e.key === "Enter") {
        handleNext(e);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [email, password, step]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!agreedToEssential) {
      alert("You must agree to the essential data collection to continue.");
      return;
    }
    setLoading(true);
    console.log("Starting sign-up process");
    try {
      console.log("Calling createUser function with data:", {
        email,
        firstName,
        lastName,
        username,
        role: "student",
        isNewSignUp: true,
        optedIntoOptional: agreedToOptional,
      });
      const createUser = httpsCallable(functions, "createUser");
      const result = await createUser({
        email,
        password,
        firstName,
        lastName,
        username,
        role: "student",
        isNewSignUp: true,
        optedIntoOptional: agreedToOptional,
      });

      console.log("Create user result:", result);

      if (result.data && result.data.uid) {
        console.log("User created successfully, attempting to sign in");
        try {
          await signInWithEmailAndPassword(auth, email, password);
          console.log("Sign-in successful, navigating");
          navigate("/home");
        } catch (signInError) {
          console.error("Error signing in after user creation:", signInError);
          alert(
            "User created successfully, but automatic sign-in failed. Please try logging in manually."
          );
          navigate("/login");
        }
      } else {
        console.error("User creation failed, no UID returned");
        throw new Error("User creation failed: No UID returned");
      }
    } catch (error) {
      console.error("Detailed error:", error);
      if (error.details) {
        console.error("Error details:", error.details);
      }
      alert(`Failed to sign up: ${error.message}`);
    } finally {
      setLoading(false);
      console.log("Sign-up process completed");
    }
  };

  const handleMicrosoftSignUp = async () => {
    if (!agreedToEssential) {
      alert("You must agree to the essential data collection to continue.");
      return;
    }
    const provider = new OAuthProvider("microsoft.com");
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("Microsoft sign-in successful:", user);

      const createUser = httpsCallable(functions, "createUser");
      const userData = {
        email: user.email,
        firstName: user.displayName ? user.displayName.split(" ")[0] : "",
        lastName: user.displayName
          ? user.displayName.split(" ").slice(1).join(" ")
          : "",
        username: user.email.split("@")[0], // Create a default username from email
        role: "student",
        providerId: "microsoft.com",
        isNewSignUp: true,
        optedIntoOptional: agreedToOptional,
      };

      const response = await createUser(userData);
      console.log("CreateUser response:", response.data);

      // Navigate to home page regardless of role
      navigate("/home");
    } catch (error) {
      console.error("Detailed error:", error);
      if (error.details) {
        console.error("Error details:", error.details);
      }
      alert(`Failed to sign up with Microsoft: ${error.message}`);
    }
  };

  return (
    <div className="signup-page">
      <h1 className="signup-title">
        {step === 1 ? "Register" : "Almost there! One last thing..."}
      </h1>
      <form onSubmit={handleSignUp} className="signup-form">
        {step === 1 && (
          <>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="signup-input"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="signup-input"
            />
            <button type="button" className="button" onClick={handleNext}>
              Sign Up!
            </button>
            <div className="legal-checkboxes">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="essential"
                  checked={agreedToEssential}
                  onChange={(e) => setAgreedToEssential(e.target.checked)}
                  required
                />
                <label htmlFor="essential">
                  I agree to the collection of essential data (name, email,
                  course data) and accept the{" "}
                  <Link to="/legal?section=terms" target="_blank">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/legal?section=privacy" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="optional"
                  checked={agreedToOptional}
                  onChange={(e) => setAgreedToOptional(e.target.checked)}
                />
                <label htmlFor="optional">
                  I agree to the collection of optional data for personalization
                  (learning preferences, avatar) as described in the{" "}
                  <Link to="/legal?section=privacy" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>
            <p className="signup-text">
              To sign up automatically with your Microsoft account:
            </p>
            <button
              type="button"
              onClick={handleMicrosoftSignUp}
              className="button signup-microsoft-button"
              disabled={!agreedToEssential}
            >
              <img src={microsoftLogo} alt="Microsoft Logo" />
              Sign Up with Microsoft
            </button>
          </>
        )}
        {step === 2 && (
          <>
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="signup-input"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="signup-input"
            />
            <button
              type="submit"
              className="button"
              disabled={loading || !agreedToEssential}
            >
              {loading ? "Signing Up..." : "Complete Sign Up"}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default SignUp;
